import { Box, Grid, Typography } from '@mui/material';
import { toast } from 'components/common/Toast';
import {
  useBrandDetails,
  usePostDetailNavigate,
} from 'features/socialMediaListening';
import { SuggestedComment } from 'features/socialMediaListening/views/postDetail/sections/suggestedComment/SuggestedComment';
import { TopicLabel } from 'features/topic';
import { useGetSocialPostForSocialMediaListeningPostDetailQuery } from 'graphql/generated';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlotRoutes } from 'Routes';
import { theme } from 'styles/theme';
import {
  SocialMediaListeningPostAnalysisView,
  SocialMediaListeningPostAnalysisViewProps,
} from './SocialMediaListeningPostAnalysisView';
import { SocialMediaListeningPostDetailViewSkeleton } from './SocialMediaListeningPostDetailViewSkeleton';
import { TitleWithInfoTooltip } from './TitleWithInfoTooltip';

export type SocialMediaListeningPostDetailViewProps = {
  brandId: string;
  socialPostId: string;
  // query params from the listening page to maintain the applied filters and pagination
  searchParams?: URLSearchParams;
  componentProps?: {
    modalView?: boolean;
    socialMediaListeningPostAnalysisView?: Partial<SocialMediaListeningPostAnalysisViewProps>;
  };
  showTopicSection?: boolean;
};

export const SocialMediaListeningPostDetailView = ({
  brandId,
  socialPostId: postId,
  searchParams,
  componentProps,
  showTopicSection = true,
}: SocialMediaListeningPostDetailViewProps) => {
  const { renderLeftArrow, renderRightArrow, topicIds } = usePostDetailNavigate(
    {
      brandId,
      searchParams: searchParams || new URLSearchParams(),
    },
  );
  const navigate = useNavigate();
  const { brand, brandNotFound, isLoading } = useBrandDetails({ brandId });

  useEffect(() => {
    if (!isLoading && brandNotFound) {
      // if the brand is not found, navigate to the home page, and show a toast have no access to the brand
      toast({
        type: 'error',
        message: 'You have no access to this brand',
      });
      navigate(PlotRoutes.home());
    }
  }, [brandNotFound]);

  const skipGetSocialPost = !postId || !brandId;

  const { data: socialPostData, loading } =
    useGetSocialPostForSocialMediaListeningPostDetailQuery({
      variables: {
        socialPostId: postId,
        topicIds: topicIds as string[],
        brandId: brandId ?? '',
      },
      skip: skipGetSocialPost,
    });

  const post = socialPostData?.socialPost;

  if (!post || loading) {
    return (
      <SocialMediaListeningPostDetailViewSkeleton
        isModalView={!!componentProps?.modalView}
      />
    );
  }

  const socialPostEngagementSuggestions =
    (topicIds as string[])?.length &&
    post.socialPostEngagementSuggestionsWithTopic.length
      ? post.socialPostEngagementSuggestionsWithTopic
      : post.socialPostEngagementSuggestions;
  const allTopics = socialPostEngagementSuggestions?.map(
    (suggestion) => suggestion.topic,
  );

  const socialPostEngagementSuggestion = socialPostEngagementSuggestions?.[0];
  const reason = socialPostEngagementSuggestion?.reason;

  const isModalView = !!componentProps?.modalView;

  return (
    <Box mt={isModalView ? 0 : 16} mx="auto">
      {!componentProps?.modalView && (
        <>
          {renderLeftArrow()}
          {renderRightArrow()}
        </>
      )}
      <Box py={isModalView ? 0 : 16}>
        <Grid container spacing={4}>
          <Grid
            item
            xs={12}
            md={isModalView ? 12 : 8}
            lg={isModalView ? 12 : 7}
            mx="auto"
          >
            <Box display="flex" flexDirection="column" gap={4}>
              {showTopicSection && (
                <Box
                  sx={{
                    display: 'flex',
                    flex: 1,
                    justifyContent: 'center',
                    gap: 8,
                    backgroundColor: theme.colors?.utility[250],
                    p: theme.spacing(6, 9),
                    borderRadius: 4,
                  }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      flex: 2,
                      flexDirection: 'column',
                      gap: 2,
                    }}
                  >
                    <TitleWithInfoTooltip
                      title="Topic"
                      info="Topic refers to a specific subject being monitored and analyzed across social media platforms."
                    />
                    <Box
                      sx={{
                        flex: 1,
                        display: 'flex',
                        gap: 2,
                        mt: 4,
                        flexDirection: 'column',
                      }}
                    >
                      <Typography
                        variant="headline-lg"
                        color={theme.colors?.primary.black}
                      >
                        {allTopics?.[0]?.name ? allTopics[0].name : ''}
                      </Typography>
                    </Box>
                    {allTopics?.[0] && (
                      <Box mt={3}>
                        <TopicLabel
                          topic={allTopics?.[0]}
                          sx={{ fontSize: 14 }}
                        />
                      </Box>
                    )}
                  </Box>

                  <Box
                    sx={{
                      width: '1px',
                      borderRight: `1px solid ${theme.colors?.utility[300]}`,
                    }}
                  />

                  <Box
                    sx={{
                      position: 'relative',
                      flex: 5,
                      flexDirection: 'column',
                      gap: 2,
                    }}
                  >
                    <TitleWithInfoTooltip
                      title="Topic Relevancy"
                      info="Topic relevancy indicates how closely your query matches the topic, helping you decide whether to engage. This ensures high confidence in the content's relevance."
                    />
                    <Box
                      sx={{
                        flex: 1,
                        display: 'flex',
                        gap: 2,
                        mt: 2,
                        flexDirection: 'column',
                      }}
                    >
                      <Typography
                        variant="headline-lg"
                        color={theme.colors?.primary.black}
                      >
                        {socialPostEngagementSuggestion?.score && (
                          <>
                            {parseFloat(
                              (
                                (socialPostEngagementSuggestion?.score || 0) *
                                100
                              ).toFixed(1),
                            )}
                            %
                          </>
                        )}
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          width: '100%',
                        }}
                      >
                        <Typography
                          color={theme.colors?.utility[700]}
                          variant="body-xl"
                          flex={1}
                        >
                          {reason}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}

              <SocialMediaListeningPostAnalysisView
                socialPost={post}
                brand={brand}
                topicIds={topicIds}
                {...componentProps?.socialMediaListeningPostAnalysisView}
              />

              {brandId && <SuggestedComment brandId={brandId} post={post} />}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};
